import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Banknote, Eye, Handshake } from 'lucide-react';
import { apiRequests } from '../../shared/api/apiRequests';
import { Pagination } from '../../features/Pagination';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';
import { EmptyState } from '../../shared/ui/EmptyState';

const PAGE_SIZE = 20;

// 'true' | 'false' из URL → boolean | null
const parseSigned = (value) => (value === 'true' ? true : value === 'false' ? false : null);

const ReferralMembers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, offset, pageSize, setPage } = useUrlPagination(PAGE_SIZE);
  // Применённые поиск и фильтр — в URL
  const query = searchParams.get('q') || '';
  const isAgreementSigned = parseSigned(searchParams.get('isAgreementSigned'));

  const [members, setMembers] = useState({ items: [], totalCount: 0 });
  // Черновики в полях
  const [searchValue, setSearchValue] = useState(query);
  const [filterValues, setFilterValues] = useState({ isAgreementSigned });
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => setSearchValue(query), [query]);
  useEffect(() => setFilterValues({ isAgreementSigned }), [isAgreementSigned]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequests.referralProgram
      .getMembers(query, isAgreementSigned, pageSize, offset)
      .then((response) => {
        if (!cancelled) setMembers(response);
      })
      .catch((e) => {
        if (!cancelled) console.error('Ошибка загрузки участников:', e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, query, isAgreementSigned]);

  // Записать поиск/фильтр в URL и вернуться на первую страницу
  const applyParams = (values) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(values).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') params.delete(key);
        else params.set(key, String(value));
      });
      params.delete('page');
      return params;
    });
  };

  const handleSearch = () =>
    applyParams({ q: searchValue.trim(), isAgreementSigned: filterValues.isAgreementSigned });

  const handleFilterChange = (key, value) => {
    if (key === 'isAgreementSigned') {
      const boolValue = value === null ? null : value === 'true';
      setFilterValues((prev) => ({ ...prev, [key]: boolValue }));
    } else {
      setFilterValues((prev) => ({ ...prev, [key]: value }));
    }
  };

  const applyFilters = handleSearch;

  const resetFilters = () => applyParams({ q: null, isAgreementSigned: null });

  const formatDate = (dateString) => {
    if (!dateString) return 'Не подписано';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const getUserName = (user) => {
    if (!user) return 'Без имени';
    const parts = [user.surname, user.name, user.patronymic].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'Без имени';
  };

  const filters = [
    {
      key: 'isAgreementSigned',
      type: 'select',
      label: 'Статус договора',
      value: filterValues.isAgreementSigned === null ? null : String(filterValues.isAgreementSigned),
      placeholder: 'Все участники',
      options: [
        { value: 'true', label: 'Договор подписан' },
        { value: 'false', label: 'Договор не подписан' },
      ],
    },
  ];

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск по ФИО..."
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={applyFilters}
        onResetFilters={resetFilters}
        rightContent={
          <Link to="/referral-program/withdrawal-requests" className="page-bar-clean__link-btn">
            <Banknote size={18} />
            <span>Заявки на выплату</span>
          </Link>
        }
      />

      <div className="mb-3">
        {loading ? (
          <p className="text-center">Загрузка...</p>
        ) : members.items?.length === 0 ? (
          query || isAgreementSigned !== null ? (
            <EmptyState
              icon={Handshake}
              variant="search"
              title="Участники не найдены"
              description="Никто не подходит под запрос и статус договора. Проверьте ФИО или сбросьте фильтр."
            />
          ) : (
            <EmptyState
              icon={Handshake}
              title="В реферальной программе пока никого"
              description="Участники появятся здесь, когда пользователи сайта подключатся к программе и начнут приводить клиентов."
            />
          )
        ) : (
          members.items?.map((member) => (
            <EntityCard
              key={member.referralUser.id}
              title={getUserName(member.referralUser.user)}
              subtitle={member.referralUser.user?.email || 'Не указан'}
              badge={member.referralUser.banInfo 
                ? { text: 'Заблокирован', variant: 'danger' }
                : member.referralUser.agreementSignedAt
                  ? { text: 'Договор подписан', variant: 'success' }
                  : { text: 'Без договора', variant: 'warning' }
              }
              fields={[
                { label: 'Заработано', value: formatCurrency(member.totalEarntInUSD) },
                { label: 'Клиентов', value: member.totalCustomers },
                { label: 'Заказов', value: member.totalOrders },
              ]}
              actions={
                <Link
                  to={`${location.pathname}/${member.referralUser.id}`}
                  className="entity-card__action"
                  title="Подробнее"
                >
                  <Eye size={20} />
                </Link>
              }
            />
          ))
        )}
      </div>

      <Pagination
        page={page}
        total={members.totalCount}
        pageSize={pageSize}
        onPageChange={setPage}
      />
    </>
  );
};

export default ReferralMembers;
